import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Checkbox, CircularProgress, FormControlLabel, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { shippingApi } from './api';

const reasons = {
  ADOPTION_DISABLED: 'New adoptions are disabled. Existing adoptions can still be reverted.',
  CONFIRMED_COMPLETE_COSTS_REQUIRED: 'Record and confirm all package costs before adoption.',
  CONSIGNMENT_PROTECTED: 'Consignment-linked order: adoption is blocked to preserve payouts.',
  PROFIT_NAMESPACE_MISMATCH: 'The financial selection belongs to another source namespace.',
  AMBIGUOUS_ITEM_ID: 'Source line identities are ambiguous. Review the order.',
  SOURCE_IDENTITY_OR_CURRENCY_INVALID: 'Valid source sale units and AUD currency are required.',
  ALLOCATION_NOT_CONSERVED: 'Complete source-line cost allocations are required.',
  PROFIT_CACHE_REFRESH_REQUIRED: 'Existing Profit caches differ from the view. Run the established Profit refresh/recalculation and review again.',
};
const aud = (v) => v == null ? 'Not established' : `AUD ${v}`;

export default function ProfitPostagePanel({ orderId, capabilities, onAccessLost }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const [reload, setReload] = useState(0);
  const alive = useRef(true);
  const sending = useRef(false);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => {
    const controller = new AbortController();
    setData(null); setConfirmed(false);
    shippingApi.profitPostage(orderId, controller.signal).then((value) => {
      if (controller.signal.aborted) return;
      if (value.order_id !== orderId) throw new Error('Preview belongs to a different order.');
      setData(value);
    }).catch((err) => {
      if (controller.signal.aborted) return;
      if (err.status === 401 || err.status === 403) onAccessLost(err);
      else setError(err.message || 'Financial preview unavailable.');
    });
    return () => controller.abort();
  }, [orderId, reload, onAccessLost]);
  async function send(action) {
    if (sending.current) return;
    if (!window.crypto?.getRandomValues) { setError('Secure request IDs require a supported browser.'); return; }
    const bytes = new Uint8Array(16); window.crypto.getRandomValues(bytes);
    const key = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    const command = pending || { action, payload: { request_key: `profit-${key}`,
      preview_sha256: data.preview_sha256, confirmed: true } };
    sending.current = true; setPending(command); setBusy(true); setError('');
    try {
      await shippingApi.profitPostageCommand(orderId, command.action, command.payload, capabilities.csrf_token);
      if (!alive.current) return;
      setPending(null); setConfirmed(false); setReload((v) => v + 1);
    } catch (err) {
      if (!alive.current) return;
      if (err.status === 401 || err.status === 403) onAccessLost(err);
      // A server/network failure may have committed. Retry the exact command;
      // never issue a fresh financial action while this outcome is unknown.
      if (err.status >= 400 && err.status < 500) { setPending(null); setConfirmed(false); setReload((v) => v + 1); }
      setError(err.message || 'Outcome unknown. Retry the same request.');
    } finally {
      sending.current = false;
      if (alive.current) setBusy(false);
    }
  }
  return <Stack gap={1}>
    <Typography variant="h6">Actual postage in Profits</Typography>
    <Alert severity="info">Adopt replaces the postage deduction for this order only. A saved version stays in use until you update or revert it. Buyer revenue, COGS and fees are unchanged.</Alert>
    {error && <Alert severity="error">{error}</Alert>}
    {!data && !error && <CircularProgress size={24} aria-label="Reading financial preview" />}
    {data && <>
      <Typography>Legacy estimate: {aud(data.legacy_order_postage)} · Current: {aud(data.current_order_postage)} · Proposed: {aud(data.proposed_order_postage)}</Typography>
      <Typography>Order profit change: {aud(data.order_profit_delta)} · Cost coverage: {data.coverage}</Typography>
      {data.active_version_id && <Typography>Adoption version {data.active_version_id} · {data.effective ? 'In use' : 'Legacy fallback: source eligibility changed'}</Typography>}
      {data.ledger_changed_since_adoption && <Alert severity="warning">Package costs or confirmations changed. The saved adoption has not been updated.</Alert>}
      {data.blockers.map((reason) => <Alert key={reason} severity="warning">{reasons[reason] || reason}</Alert>)}
      <Table size="small" aria-label="Postage financial comparison"><TableHead><TableRow>
        <TableCell>Source sale line / units</TableCell><TableCell>Current postage</TableCell><TableCell>Proposed postage</TableCell><TableCell>Item profit change</TableCell>
      </TableRow></TableHead><TableBody>{data.lines.map((line) => <TableRow key={line.source_order_item_id}>
        <TableCell>{line.item_id} · {line.sale_unit_quantity}</TableCell><TableCell>{aud(line.current_postage)}</TableCell>
        <TableCell>{aud(line.proposed?.item_ship_alloc)}</TableCell><TableCell>{aud(line.profit_delta)}</TableCell>
      </TableRow>)}</TableBody></Table>
      <Typography variant="caption">{data.consignment_note} Item totals retain legacy rounding and ad-fee treatment; the order delta uses the order view.</Typography>
      <FormControlLabel control={<Checkbox checked={confirmed} disabled={busy || !!pending} onChange={(event) => setConfirmed(event.target.checked)} />}
        label="I reviewed the order and line deltas. Apply replaces postage; Revert restores the current legacy estimate." />
      <Stack direction="row" gap={1}>
        <Button variant="contained" disabled={!confirmed || !data.can_apply || busy || !!pending} onClick={() => send('apply')}>{data.active_version_id ? 'Update adopted costs' : 'Adopt actual costs'}</Button>
        <Button disabled={!confirmed || !data.can_revert || busy || !!pending} onClick={() => send('revert')}>Revert to legacy estimate</Button>
      </Stack>
    </>}
    {pending && <Button disabled={busy} onClick={() => send(pending.action)}>Retry same financial request</Button>}
    <Button disabled={busy} onClick={() => { setError(''); setReload((v) => v + 1); }}>Reload financial preview</Button>
  </Stack>;
}
